import React, { useState, useEffect, useRef } from "react";
import { getAuth } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import Autocomplete from "react-google-autocomplete";
import { firestore } from "../../firebase";
import LocationQuickEditPanel from "./editLocationComponents/locationQuickEditPanel";
import {
  createLocation,
  convertGoogleOpeningHours,
  emptyClosingHours,
  emptyOpeningHours,
  fetchGoogleBusinessDetails,
} from "./utils/createLocation";
import {
  scrapeSiteInfo,
  uploadImageFromUrl,
  facebookPictureUrl,
} from "./utils/scrapeSiteInfo";
import { runMenuScrapeChain } from "./utils/menuScrapeChain";
import { buildTagsLang } from "./utils/buildTagsLang";
import languagesShortened from "../../assets/assetComponents/languagesShortened";
import { translateBatch } from "./utils/translateBatch";
import {
  saveTripadvisorRating,
  saveYelpRating,
  tripadvisorIdFromLink,
  yelpIdFromLink,
} from "./utils/reviewSources";
import "../../css/quickReviewLocations.css";

const auth = getAuth();
const db = firestore;

// Hidden from the sidebar on purpose, reached only via a direct link
// (/MyBusiness/quickAdd). Open to admins/moderators plus this account.
const ALLOWED_EMAIL = "steffen@flyteori.no";

// The 4 profileImages slots used by editMyLocation.js. The main image is
// picked separately (usually the Facebook/Instagram profile picture).
const MAX_GALLERY_IMAGES = 4;
// Images smaller than this (in either dimension) are hidden by default —
// mostly logos, icons and thumbnails.
const SMALL_IMAGE_PX = 200;

// Guess type/secondaryType from Google's place types so most queue items
// need no clicking. Always editable on the card.
function guessTypeFromGoogle(types = []) {
  const has = (...t) => t.some((x) => types.includes(x));
  if (has("cafe")) return { locationType: "restaurant", locationSecondaryType: "Cafe" };
  if (has("bar", "night_club")) return { locationType: "restaurant", locationSecondaryType: "Bar" };
  if (has("restaurant", "food", "bakery", "meal_takeaway", "meal_delivery")) {
    return { locationType: "restaurant", locationSecondaryType: "" };
  }
  if (
    has(
      "tourist_attraction",
      "museum",
      "amusement_park",
      "bowling_alley",
      "gym",
      "park",
      "zoo",
      "aquarium",
      "art_gallery",
      "movie_theater",
      "spa",
      "stadium",
    )
  ) {
    return { locationType: "activity", locationSecondaryType: "" };
  }
  return { locationType: "restaurant", locationSecondaryType: "" };
}

const newItem = (placeId) => ({
  key: `${placeId}-${Date.now()}`,
  placeId,
  open: true,
  form: {
    locationType: "restaurant",
    locationSecondaryType: "",
    locationName: "",
    latitude: "",
    longitude: "",
    website: "",
    googleBusinessDetails: null,
    regularOpeningHours: emptyOpeningHours(),
    mainTags: "", // comma-separated, in the location's own language
    phone: "",
    bookingLink: "",
    tripadvisorLink: "",
    yelpLink: "",
  },
  loadingDetails: true,
  scan: null, // { loading, error, result }
  imageSizes: {}, // url -> { w, h } | "broken"
  // { kind: "url", url, auto } | { kind: "file", file, preview } — auto means
  // it was guessed from the Facebook link and can be replaced on rescan.
  mainImage: null,
  selectedImages: [], // ordered urls for the profileImages slots
  description: "",
  menu: null, // { url, applying, result }
  createStatus: null, // { step, error, done, failedUploads }
  createdLocationId: null,
  editorOpen: false,
  translations: {}, // description suggestion text -> English translation
  translateStatus: null, // { loading } | { error }
});

// A pasted/chosen file is uploaded straight from the browser (same as
// useLocationEditor.js); a URL goes through the server since most sites,
// Facebook included, don't allow the browser to read their images.
async function uploadMainImage(mainImage) {
  if (mainImage.kind === "file") {
    const storageRef = ref(getStorage(), `images/${uuidv4()}`);
    const snapshot = await uploadBytes(storageRef, mainImage.file);
    return getDownloadURL(snapshot.ref);
  }
  return uploadImageFromUrl(mainImage.url);
}

// The location's language, guessed from the country in Google's address —
// the same country → language mapping createLocation uses on create.
const locationLangFromGoogle = (details) => {
  const country = details?.address_components?.find((c) => c.types?.includes("country"));
  return (country && languagesShortened[country.short_name]) || null;
};

// One DeepL request for a list of texts (billed per character, so callers
// only send texts that aren't translated yet). Returns translations in order.
async function translateTextsToEnglish(texts, fromLang) {
  const res = await fetch(
    "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/translateWithDeepL",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ texts, toLang: "en-US", fromLang }),
    },
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !Array.isArray(json.text) || json.text.length !== texts.length) {
    throw new Error(json.error || `translation failed (${res.status})`);
  }
  return json.text;
}

// Same as the editor's "Translate automatically" link for the description
// (about.js → translateWithDeepL, from the location's language to en-US),
// but done on create. Stored where the editor keeps the English text.
async function translateDescriptionToEnglish(docRef, description, lang) {
  if (lang === "en_US") {
    await updateDoc(docRef, { "aboutBusinessLang.en_US": description });
    return;
  }
  // Called directly rather than via translateBatch, which hides failures by
  // returning the input — here a failure must not be stored as "English".
  // (Unchanged output is fine: scraped descriptions are often English already.)
  const res = await fetch(
    "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/translateWithDeepL",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ texts: [description], toLang: "en-US", fromLang: lang }),
    },
  );
  const json = await res.json().catch(() => ({}));
  const translated = Array.isArray(json.text) ? json.text[0] : null;
  if (!res.ok || typeof translated !== "string" || !translated.trim()) {
    throw new Error(json.error || `translation failed (${res.status})`);
  }
  await updateDoc(docRef, { "aboutBusinessLang.en_US": translated });
}

const getMissing = (item) => {
  const missing = [];
  if (item.form.locationType === "") missing.push("type");
  if (item.form.locationName.trim() === "") missing.push("name");
  if (item.form.latitude === "" || item.form.longitude === "") missing.push("coordinates");
  // A link we can't read an id from would be saved but never get a rating.
  if (item.form.tripadvisorLink.trim() && !tripadvisorIdFromLink(item.form.tripadvisorLink)) {
    missing.push("valid Tripadvisor link");
  }
  if (item.form.yelpLink.trim() && !yelpIdFromLink(item.form.yelpLink)) {
    missing.push("valid Yelp link");
  }
  return missing;
};

const isBusy = (item) =>
  item.loadingDetails || item.scan?.loading || item.menu?.applying || !!item.createStatus?.step;

const canCreate = (item) =>
  !item.createdLocationId && !isBusy(item) && getMissing(item).length === 0;

// Faster "add location" workflow: search Google to add places to a queue.
// Each queued place gets its website read in the background (images,
// description, menu links), so you can pick what to keep on each card and
// create one or all of them without leaving the page. After creating, the
// full quick-edit panel (About + Menu + Reviews) can be opened on the card.
const QuickAddLocation = ({ currentBusinessID }) => {
  const [currentUserEmail, setCurrentUserEmail] = useState(undefined);
  const [userInformation, setUserInformation] = useState(null);

  const [items, setItems] = useState([]);
  const [searchKey, setSearchKey] = useState(0);
  const [hideSmallImages, setHideSmallImages] = useState(true);
  const [creatingAll, setCreatingAll] = useState(false);
  const [notice, setNotice] = useState(null);
  // Translate description suggestions as soon as a site is read. Off by
  // default (DeepL is billed per character); remembered per browser.
  const [autoTranslate, setAutoTranslate] = useState(() => {
    try {
      return localStorage.getItem("quickAddAutoTranslate") === "1";
    } catch {
      return false;
    }
  });
  const autoTranslateRef = useRef(autoTranslate);
  autoTranslateRef.current = autoTranslate;

  // Async handlers (scan, create) read the latest item state from here.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  // Per-item counter so a slow earlier scan can't overwrite a newer rescan.
  const scanTokens = useRef({});
  // Keys of cards currently being created (see createItem).
  const creatingKeys = useRef(new Set());

  useEffect(() => {
    auth.onAuthStateChanged(async function (user) {
      setCurrentUserEmail(user ? user.email : null);
      if (user) {
        const userInfo = await getDoc(doc(db, "users", user.uid));
        setUserInformation(userInfo.data());
      }
    });
  }, []);

  const updateItem = (key, patch) =>
    setItems((prev) =>
      prev.map((it) =>
        it.key === key ? { ...it, ...(typeof patch === "function" ? patch(it) : patch) } : it,
      ),
    );

  const updateForm = (key, fields) =>
    updateItem(key, (it) => ({ form: { ...it.form, ...fields } }));

  const getItem = (key) => itemsRef.current.find((it) => it.key === key);

  // Translate the website's description suggestions so you can read them
  // before picking one. Only texts without a translation yet are sent, and
  // the result is kept per card, so clicking again (or a re-render) is free.
  const translateSuggestions = async (key, texts, lang) => {
    const item = getItem(key);
    const fromLang = lang || locationLangFromGoogle(item?.form.googleBusinessDetails);
    const all = texts || item?.scan?.result?.descriptions.map((d) => d.text) || [];
    const known = item?.translations || {};
    const missing = all.filter((t) => !known[t]);
    if (!fromLang || fromLang === "en_US" || missing.length === 0) return;

    const token = scanTokens.current[key];
    updateItem(key, { translateStatus: { loading: true } });
    try {
      const translated = await translateTextsToEnglish(missing, fromLang);
      if (scanTokens.current[key] !== token) return; // rescanned meanwhile
      updateItem(key, (it) => ({
        translations: {
          ...it.translations,
          ...Object.fromEntries(missing.map((t, i) => [t, translated[i]])),
        },
        translateStatus: null,
      }));
    } catch (err) {
      if (scanTokens.current[key] !== token) return;
      updateItem(key, { translateStatus: { error: err.message } });
    }
  };

  const runScan = async (key, url) => {
    if (!url) return;
    const token = (scanTokens.current[key] || 0) + 1;
    scanTokens.current[key] = token;
    updateItem(key, (it) => ({
      scan: { loading: true },
      imageSizes: {},
      selectedImages: [],
      description: "",
      menu: null,
      translations: {},
      translateStatus: null,
      // Keep a main image someone picked by hand; drop an auto-guessed one.
      mainImage: it.mainImage?.auto ? null : it.mainImage,
    }));
    try {
      const result = await scrapeSiteInfo(url);
      if (scanTokens.current[key] !== token) return;
      const firstFacebook = result.socialLinks.facebook[0];
      // Only known booking providers (OpenTable, Resy...) are filled in
      // automatically; generic "Book" links are shown as suggestions.
      const knownBooking = result.bookingLinks.find((b) => b.provider);
      updateItem(key, (it) => ({
        form: {
          ...it.form,
          phone: it.form.phone || result.phones[0] || "",
          bookingLink: it.form.bookingLink || knownBooking?.url || "",
          tripadvisorLink:
            it.form.tripadvisorLink || result.reviewLinks.tripadvisor[0]?.url || "",
          yelpLink: it.form.yelpLink || result.reviewLinks.yelp[0]?.url || "",
        },
        scan: { loading: false, result },
        // Pre-fill the best description guess; easy to change on the card.
        description: result.descriptions[0]?.text || "",
        // Default the main image to the Facebook profile picture. If the
        // guess doesn't load, the preview's onError clears it again.
        mainImage:
          it.mainImage ||
          (firstFacebook
            ? { kind: "url", url: facebookPictureUrl(firstFacebook.pictureId), auto: true }
            : null),
      }));
      if (autoTranslateRef.current && result.descriptions.length > 0) {
        translateSuggestions(
          key,
          result.descriptions.map((d) => d.text),
          locationLangFromGoogle(getItem(key)?.form.googleBusinessDetails),
        );
      }
    } catch (err) {
      if (scanTokens.current[key] !== token) return;
      updateItem(key, { scan: { loading: false, error: err.message } });
    }
  };

  const onPlaceSelected = async (place) => {
    // Remount the Autocomplete so its input clears for the next search.
    setSearchKey((k) => k + 1);
    setNotice(null);

    if (!place?.place_id || !place.geometry) return;

    const existing = itemsRef.current.find((it) => it.placeId === place.place_id);
    if (existing) {
      setNotice(`"${existing.form.locationName || place.name}" is already in the queue.`);
      updateItem(existing.key, { open: true });
      return;
    }

    const item = newItem(place.place_id);
    item.form.latitude = place.geometry.location.lat();
    item.form.longitude = place.geometry.location.lng();
    item.form.locationName = place.name || "";
    setItems((prev) => [item, ...prev]);

    try {
      const details = await fetchGoogleBusinessDetails(place.place_id);
      updateItem(item.key, (it) => ({
        loadingDetails: false,
        form: {
          ...it.form,
          ...guessTypeFromGoogle(details.types),
          googleBusinessDetails: details,
          locationName: details.name || it.form.locationName,
          website: details.website || "",
          phone:
            it.form.phone ||
            details.international_phone_number ||
            details.formatted_phone_number ||
            "",
          regularOpeningHours: convertGoogleOpeningHours(details.opening_hours),
        },
      }));
      if (details.website) runScan(item.key, details.website);
    } catch (err) {
      console.error("Google business details failed:", err);
      updateItem(item.key, { loadingDetails: false });
    }
  };

  const applyMenuCandidate = async (key, candidateUrl) => {
    updateItem(key, { menu: { url: candidateUrl, applying: true, result: null } });
    const result = await runMenuScrapeChain({
      url: candidateUrl,
      ignoreTerms: "",
      includeTerms: "",
    });
    updateItem(key, { menu: { url: candidateUrl, applying: false, result } });
  };

  const createItem = async (key) => {
    const item = getItem(key);
    // creatingKeys catches a double-click (or "Create all" overlapping a
    // card's own button) before the "busy" state has re-rendered.
    if (!item || !canCreate(item) || creatingKeys.current.has(key)) return;
    creatingKeys.current.add(key);
    const { form, mainImage, selectedImages, description, menu } = item;

    try {
      // Re-upload the picked images first so the new doc is written once,
      // with the final Firebase URLs.
      const failedUploads = [];
      const imageCount = selectedImages.length + (mainImage ? 1 : 0);
      if (imageCount > 0) {
        updateItem(key, {
          createStatus: { step: `Uploading ${imageCount} images...` },
        });
      }
      const [mainResult, ...galleryResults] = await Promise.allSettled([
        mainImage ? uploadMainImage(mainImage) : Promise.resolve(null),
        ...selectedImages.map((url) => uploadImageFromUrl(url)),
      ]);

      let mainProfileImage = null;
      if (mainResult.status === "fulfilled") mainProfileImage = mainResult.value;
      else failedUploads.push(`Main image: ${mainResult.reason?.message}`);

      const gallery = [];
      galleryResults.forEach((r, i) => {
        if (r.status === "fulfilled") gallery.push(r.value);
        else failedUploads.push(`${selectedImages[i]}: ${r.reason?.message}`);
      });

      updateItem(key, { createStatus: { step: "Creating location..." } });

      const profileImages = [0, 1, 2, 3].map((i) => gallery[i] || "");
      const menuResult = menu?.result?.success ? menu.result : null;
      const aboutBusiness = description.trim();

      const phoneNumber = form.phone.trim();
      const bookingLink = form.bookingLink.trim();
      const tripadvisorLink = form.tripadvisorLink.trim();
      const yelpLink = form.yelpLink.trim();
      const mainTags = form.mainTags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      const { id, lang } = await createLocation({
        currentBusinessID,
        userInformation,
        locationName: form.locationName.trim(),
        locationType: form.locationType,
        locationSecondaryType: form.locationSecondaryType,
        latitude: form.latitude,
        longitude: form.longitude,
        googleBusinessDetails: form.googleBusinessDetails,
        regularOpeningHours: form.regularOpeningHours,
        regularClosingHours: emptyClosingHours(),
        extraFields: (lang) => ({
          website: form.website.trim() || form.googleBusinessDetails?.website || null,
          ...(mainProfileImage && { mainProfileImage }),
          ...(gallery.length > 0 && { profileImages }),
          ...(aboutBusiness && {
            aboutBusiness,
            aboutBusinessLang: { [lang]: aboutBusiness },
          }),
          ...(menu?.url && { menuFetchUrl: menu.url }),
          ...(menuResult && {
            menus: menuResult.menuList,
            menuListLang: { [lang]: menuResult.menuList },
            menuFetchUrlType: menuResult.method,
            menusChanged: new Date().getTime(),
          }),
          phoneNumber,
          bookingLink,
          // Same rule as useLocationEditor.js's save.
          bookingAvailable: bookingLink.length > 0 || phoneNumber.length > 0,
          locationTags: mainTags,
          // Stored even if the rating lookup below fails, so the editor shows them.
          ...(tripadvisorLink && { tripadvisorLink }),
          ...(yelpLink && { yelpLink }),
        }),
      });

      // ── After the doc exists: translated main tags + review ratings ──
      updateItem(key, { createStatus: { step: "Translating & fetching ratings..." } });
      const docRef = doc(db, "lokasjoner", id);
      const ratings = [];
      const googleRating = parseFloat(form.googleBusinessDetails?.rating);
      if (!isNaN(googleRating)) ratings.push(googleRating);

      const followUps = await Promise.allSettled([
        mainTags.length > 0
          ? buildTagsLang({ tagList: mainTags, fromLang: lang, translate: translateBatch }).then(
              (locationTagsLang) => updateDoc(docRef, { locationTagsLang }),
            )
          : null,
        tripadvisorLink ? saveTripadvisorRating(id, tripadvisorLink) : null,
        yelpLink ? saveYelpRating(id, yelpLink) : null,
        aboutBusiness ? translateDescriptionToEnglish(docRef, aboutBusiness, lang) : null,
      ]);
      const [tagsResult, tripadvisorResult, yelpResult, descriptionResult] = followUps;
      if (tagsResult.status === "rejected") {
        failedUploads.push(`Main tags translation: ${tagsResult.reason?.message}`);
      }
      if (descriptionResult.status === "rejected") {
        failedUploads.push(`English description: ${descriptionResult.reason?.message}`);
      }
      [
        ["Tripadvisor", tripadvisorResult],
        ["Yelp", yelpResult],
      ].forEach(([name, r]) => {
        if (r.status === "rejected") failedUploads.push(`${name} rating: ${r.reason?.message}`);
        else if (typeof r.value === "number") ratings.push(r.value);
      });

      // Same as the editor's save: average of the available ratings.
      if (ratings.length > 0) {
        await updateDoc(docRef, {
          overAllRating: ratings.reduce((a, b) => a + b, 0) / ratings.length,
        });
      }

      updateItem(key, {
        createdLocationId: id,
        createStatus: { step: null, done: true, failedUploads },
        open: false,
      });
    } catch (err) {
      console.error("Quick add failed:", err);
      updateItem(key, { createStatus: { step: null, error: err.message } });
    } finally {
      creatingKeys.current.delete(key);
    }
  };

  // One at a time, oldest first, so uploads don't all compete at once.
  const createAll = async () => {
    setCreatingAll(true);
    const keys = itemsRef.current.filter(canCreate).map((it) => it.key).reverse();
    for (const key of keys) {
      await createItem(key);
    }
    setCreatingAll(false);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((it) => it.key !== key));

  const clearCreated = () =>
    setItems((prev) => prev.filter((it) => !it.createdLocationId));

  if (currentUserEmail === undefined) {
    return null;
  }

  if (
    currentUserEmail !== ALLOWED_EMAIL &&
    !userInformation?.admin &&
    !userInformation?.moderator
  ) {
    return currentUserEmail && userInformation === null ? null : <>Access Denied!</>;
  }

  const readyCount = items.filter(canCreate).length;
  const createdCount = items.filter((it) => it.createdLocationId).length;

  return (
    <div>
      <h1>Quick Add Locations</h1>

      <div className='box quickReviewPanel quickAddStep'>
        <label className='editPageLabel'>Search Google to add a location to the queue</label>
        <Autocomplete
          key={searchKey}
          options={{ types: ["establishment"] }}
          placeholder='Restaurant, café, bar, activity...'
          apiKey={"AIzaSyBzqNdOb1GQUpeP2bFfsed64Yp_hEALAQ4"}
          onPlaceSelected={onPlaceSelected}
          style={{ width: "100%", boxSizing: "border-box" }}
        />
        {notice && <p className='quickAddHint'>{notice}</p>}
        <div className='quickAddToolbar'>
          <button
            className='universalButtonRed'
            disabled={readyCount === 0 || creatingAll}
            style={{ opacity: readyCount === 0 || creatingAll ? 0.5 : 1 }}
            onClick={createAll}>
            {creatingAll ? "Creating..." : `Create all ready (${readyCount})`}
          </button>
          {createdCount > 0 && (
            <button className='universalButton' onClick={clearCreated}>
              Clear created ({createdCount})
            </button>
          )}
          <label className='quickAddCheck'>
            <input
              type='checkbox'
              checked={hideSmallImages}
              onChange={(e) => setHideSmallImages(e.target.checked)}
            />
            Hide small images
          </label>
          <label
            className='quickAddCheck'
            title='Translates the description suggestions to English as soon as a website is read (uses DeepL characters)'>
            <input
              type='checkbox'
              checked={autoTranslate}
              onChange={(e) => {
                setAutoTranslate(e.target.checked);
                try {
                  localStorage.setItem("quickAddAutoTranslate", e.target.checked ? "1" : "0");
                } catch {
                  // Storage blocked — the toggle still works for this visit.
                }
              }}
            />
            Auto-translate descriptions
          </label>
          <span className='quickAddHint'>
            {items.length} in queue · new locations are created as inactive
          </span>
        </div>
      </div>

      {items.length === 0 && (
        <p className='quickAddHint'>
          The queue is empty. Search for a place above — you can add several
          before creating them.
        </p>
      )}

      {items.map((item) => (
        <QueueItem
          key={item.key}
          item={item}
          hideSmallImages={hideSmallImages}
          currentBusinessID={currentBusinessID}
          userInformation={userInformation}
          onUpdate={(patch) => updateItem(item.key, patch)}
          onUpdateForm={(fields) => updateForm(item.key, fields)}
          onRescan={() => runScan(item.key, item.form.website.trim())}
          onApplyMenu={(url) => applyMenuCandidate(item.key, url)}
          onTranslate={() => translateSuggestions(item.key)}
          onCreate={() => createItem(item.key)}
          onRemove={() => removeItem(item.key)}
        />
      ))}
    </div>
  );
};

const statusOf = (item) => {
  if (item.createdLocationId) return { label: "Created", className: "done" };
  if (item.createStatus?.step) return { label: item.createStatus.step, className: "busy" };
  if (item.createStatus?.error) return { label: "Failed", className: "error" };
  if (item.loadingDetails) return { label: "Loading Google details...", className: "busy" };
  if (item.scan?.loading) return { label: "Reading website...", className: "busy" };
  if (item.menu?.applying) return { label: "Fetching menu...", className: "busy" };
  const missing = getMissing(item);
  if (missing.length > 0) return { label: `Missing ${missing.join(", ")}`, className: "error" };
  return { label: "Ready", className: "ready" };
};

const QueueItem = ({
  item,
  hideSmallImages,
  currentBusinessID,
  userInformation,
  onUpdate,
  onUpdateForm,
  onRescan,
  onApplyMenu,
  onTranslate,
  onCreate,
  onRemove,
}) => {
  const { form, scan, imageSizes, selectedImages, description, menu } = item;
  const result = scan?.result;
  const status = statusOf(item);
  const created = !!item.createdLocationId;

  const toggleImage = (url) =>
    onUpdate((it) => ({
      selectedImages: it.selectedImages.includes(url)
        ? it.selectedImages.filter((u) => u !== url)
        : it.selectedImages.length >= MAX_GALLERY_IMAGES
          ? it.selectedImages
          : [...it.selectedImages, url],
    }));

  const setMainImage = (mainImage) => onUpdate({ mainImage });

  const setImageSize = (url, size) =>
    onUpdate((it) => ({ imageSizes: { ...it.imageSizes, [url]: size } }));

  const visibleImages = (result?.images || []).filter((img) => {
    const size = imageSizes[img.url];
    if (size === "broken") return false;
    if (!hideSmallImages || !size) return true;
    return size.w >= SMALL_IMAGE_PX && size.h >= SMALL_IMAGE_PX;
  });
  const hiddenCount = (result?.images.length || 0) - visibleImages.length;

  // Suggestions can only be translated once we know the source language
  // (from Google's country) — and there's nothing to do for English places.
  const locationLang = locationLangFromGoogle(form.googleBusinessDetails);
  const canTranslate = !!locationLang && locationLang !== "en_US";
  const untranslatedCount = (result?.descriptions || []).filter(
    (d) => !item.translations?.[d.text],
  ).length;
  const nothingFound =
    result &&
    result.images.length === 0 &&
    result.descriptions.length === 0 &&
    result.menuCandidates.length === 0;

  return (
    <div className={`box quickReviewPanel quickAddItem${item.open ? " open" : ""}`}>
      <div className='quickAddItemHeader' onClick={() => onUpdate({ open: !item.open })}>
        <span className='quickReviewChevron'>{item.open ? "▾" : "▸"}</span>
        <div className='quickAddItemTitle'>
          <strong>{form.locationName || "Untitled"}</strong>
          <span className='quickAddHint'>
            {form.googleBusinessDetails?.formatted_address || ""}
            {form.googleBusinessDetails?.rating &&
              ` · ⭐ ${form.googleBusinessDetails.rating} (${form.googleBusinessDetails.user_ratings_total})`}
          </span>
        </div>
        {!item.open && !created && (
          <span className='quickAddHint'>
            {item.mainImage ? "main image · " : "no main image · "}
            {selectedImages.length} images
            {description ? " · description" : ""}
            {menu?.url ? " · menu" : ""}
          </span>
        )}
        <span className={`quickAddStatus ${status.className}`}>{status.label}</span>
        {!created && !item.createStatus?.step && (
          <button
            className='quickAddRemove'
            title='Remove from queue'
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}>
            ✕
          </button>
        )}
      </div>

      {item.open && !created && (
        <div className='quickAddItemBody'>
          {/* ── Basics ── */}
          <div className='quickAddRow'>
            {[
              ["restaurant", "Restaurant"],
              ["activity", "Activity"],
            ].map(([value, label]) => (
              <label key={value} className='quickAddCheck'>
                <input
                  type='checkbox'
                  className='inputCheckbox'
                  checked={form.locationType === value}
                  onChange={() =>
                    onUpdateForm({
                      locationType: form.locationType === value ? "" : value,
                      locationSecondaryType: "",
                    })
                  }
                />
                {label}
              </label>
            ))}
            {form.locationType === "restaurant" &&
              [
                ["Cafe", "Café"],
                ["Bar", "Bar/Pub"],
              ].map(([value, label]) => (
                <label key={value} className='quickAddCheck quickAddSecondary'>
                  <input
                    type='checkbox'
                    className='inputCheckbox'
                    checked={form.locationSecondaryType === value}
                    onChange={() =>
                      onUpdateForm({
                        locationSecondaryType:
                          form.locationSecondaryType === value ? "" : value,
                      })
                    }
                  />
                  {label}
                </label>
              ))}
          </div>

          <div className='quickAddGrid'>
            <div className='quickAddField'>
              <label className='editPageLabel'>Name</label>
              <input
                placeholder='Name'
                value={form.locationName}
                onChange={(e) => onUpdateForm({ locationName: e.target.value })}
              />
            </div>
            <div className='quickAddField'>
              <label className='editPageLabel'>Website</label>
              <div className='quickAddInline'>
                <input
                  placeholder='https://...'
                  value={form.website}
                  onChange={(e) => onUpdateForm({ website: e.target.value })}
                />
                <button
                  className='universalButton'
                  disabled={!form.website || scan?.loading}
                  onClick={onRescan}>
                  {scan?.loading ? "Reading..." : result ? "Read again" : "Read website"}
                </button>
              </div>
            </div>
          </div>

          {form.googleBusinessDetails && !form.googleBusinessDetails.website && (
            <p className='quickAddHint'>
              Google has no website for this place. Paste one above and click
              "Read website".
            </p>
          )}
          {scan?.error && (
            <p className='quickAddError'>Could not read the website: {scan.error}</p>
          )}
          {nothingFound && (
            <p className='quickAddError'>
              Nothing found on the website. It probably blocks automated reading
              or loads its content with JavaScript — add images and text in the
              editor after creating the location.
            </p>
          )}

          {!item.loadingDetails && (
            <MainImagePicker
              mainImage={item.mainImage}
              onChange={setMainImage}
              socialLinks={result?.socialLinks}
              scanned={!!result || !!scan?.error || !form.website}
              locationName={form.locationName}
            />
          )}

          {result && (
            <>
              {/* ── Website images → profile images ── */}
              <div className='quickReviewSection'>
                <div className='quickReviewSectionHeader'>
                  Profile images from the website ({selectedImages.length}/
                  {MAX_GALLERY_IMAGES} selected)
                  {hiddenCount > 0 && (
                    <span className='quickAddHint'>· {hiddenCount} small hidden</span>
                  )}
                </div>
                <div className='quickReviewSectionBody'>
                  <p className='quickAddHint' style={{ marginTop: 0 }}>
                    Click to select up to 4 profile images. Pages read:{" "}
                    {result.pagesScanned.join(", ")}
                  </p>
                  {visibleImages.length === 0 ? (
                    <p>No images found.</p>
                  ) : (
                    <div className='quickAddImageGrid'>
                      {visibleImages.map((img) => {
                        const index = selectedImages.indexOf(img.url);
                        const size = imageSizes[img.url];
                        const isMain =
                          item.mainImage?.kind === "url" && item.mainImage.url === img.url;
                        return (
                          <div
                            key={img.url}
                            className={`quickAddImage${index >= 0 ? " selected" : ""}`}
                            onClick={() => toggleImage(img.url)}
                            title={img.url}>
                            <img
                              src={img.url}
                              alt=''
                              loading='lazy'
                              referrerPolicy='no-referrer'
                              onLoad={(e) =>
                                setImageSize(img.url, {
                                  w: e.target.naturalWidth,
                                  h: e.target.naturalHeight,
                                })
                              }
                              onError={() => setImageSize(img.url, "broken")}
                            />
                            {index >= 0 && (
                              <span className='quickAddImageBadge'>{index + 1}</span>
                            )}
                            <button
                              className='quickAddMakeMain'
                              disabled={isMain}
                              onClick={(e) => {
                                e.stopPropagation();
                                setMainImage({ kind: "url", url: img.url });
                              }}>
                              {isMain ? "Main image" : "Use as main"}
                            </button>
                            <span className='quickAddImageInfo'>
                              {size && size !== "broken" ? `${size.w}×${size.h}` : ""}
                              {img.page !== "home page" ? ` · ${img.page}` : ""}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Description ── */}
              <div className='quickReviewSection'>
                <div className='quickReviewSectionHeader'>Description</div>
                <div className='quickReviewSectionBody'>
                  <textarea
                    className='quickAddDescription'
                    value={description}
                    placeholder='Pick a text below, or write your own'
                    onChange={(e) => onUpdate({ description: e.target.value })}
                  />
                  <p className='quickAddHint' style={{ marginTop: 0 }}>
                    An English version is translated automatically when the
                    location is created.
                  </p>
                  {canTranslate && result.descriptions.length > 0 && (
                    <div className='quickAddTranslateRow'>
                      {untranslatedCount > 0 ? (
                        <button
                          className='universalButton'
                          disabled={!!item.translateStatus?.loading}
                          onClick={onTranslate}>
                          {item.translateStatus?.loading
                            ? "Translating..."
                            : `Translate ${untranslatedCount} suggestion${
                                untranslatedCount === 1 ? "" : "s"
                              } to English`}
                        </button>
                      ) : (
                        <span className='quickAddHint'>
                          Suggestions translated from {locationLang} — the original
                          text is what gets saved.
                        </span>
                      )}
                      {item.translateStatus?.error && (
                        <span className='quickAddError'>
                          Translation failed: {item.translateStatus.error}
                        </span>
                      )}
                    </div>
                  )}
                  {result.descriptions.length === 0 ? (
                    <p>No descriptions found.</p>
                  ) : (
                    <ul className='quickAddDescriptionList'>
                      {result.descriptions.map((d, i) => (
                        <li key={i} className={description === d.text ? "selected" : ""}>
                          <span className='quickAddSource'>
                            {d.source === "text" ? d.page : d.source}
                          </span>
                          <span className='quickAddDescriptionText'>
                            {d.text}
                            {item.translations?.[d.text] &&
                              item.translations[d.text] !== d.text && (
                                <span className='quickAddTranslation'>
                                  EN: {item.translations[d.text]}
                                </span>
                              )}
                          </span>
                          <button
                            className='universalButton'
                            onClick={() => onUpdate({ description: d.text })}>
                            Use
                          </button>
                          <button
                            className='universalButton'
                            onClick={() =>
                              onUpdate((it) => ({
                                description: it.description.trim()
                                  ? `${it.description.trim()}\n\n${d.text}`
                                  : d.text,
                              }))
                            }>
                            Append
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* ── Menu ── */}
              <div className='quickReviewSection'>
                <div className='quickReviewSectionHeader'>Menu</div>
                <div className='quickReviewSectionBody'>
                  {result.menuCandidates.length === 0 ? (
                    <p>No menu links found on the home page.</p>
                  ) : (
                    <ul className='quickAddMenuList'>
                      {result.menuCandidates.map((c, i) => (
                        <li key={i} className={menu?.url === c.url ? "selected" : ""}>
                          <a href={c.url} target='_blank' rel='noreferrer'>
                            {c.url}
                          </a>{" "}
                          <span className='quickAddSource'>
                            {c.type} · {c.score}
                          </span>
                          <button
                            className='universalButton'
                            disabled={!!menu?.applying}
                            onClick={() => onApplyMenu(c.url)}>
                            {menu?.applying && menu.url === c.url
                              ? "Fetching..."
                              : "Fetch menu"}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {menu?.result && (
                    <p
                      className={`quickReviewMenuLinkTestApplyResult${
                        menu.result.success ? " success" : " fail"
                      }`}>
                      {menu.result.success
                        ? `✅ Found a menu (${menu.result.menuList.length} categories) via ${menu.result.method} — saved when you create the location.`
                        : `❌ No menu found automatically. The link is still saved as the menu URL.`}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {!item.loadingDetails && (
            <DetailsSection form={form} result={result} onUpdateForm={onUpdateForm} />
          )}

          <div className='quickAddCreateRow'>
            <button
              className='universalButtonRed'
              style={{ opacity: canCreate(item) ? 1 : 0.5 }}
              disabled={!canCreate(item)}
              onClick={onCreate}>
              {item.createStatus?.step || "Create location"}
            </button>
            {item.createStatus?.error && (
              <span className='quickAddError'>Error: {item.createStatus.error}</span>
            )}
          </div>
        </div>
      )}

      {created && (
        <div className='quickAddItemBody'>
          <div className='quickAddDone'>
            <span>
              ✅ Created as inactive. Open the editor to add tags, then save
              (and activate when ready).
            </span>
            {item.createStatus?.failedUploads?.length > 0 && (
              <p className='quickAddError'>
                Created, but {item.createStatus.failedUploads.length} thing(s) didn't
                work — fix them in the editor:
                <br />
                {item.createStatus.failedUploads.map((f) => (
                  <span key={f}>
                    {f}
                    <br />
                  </span>
                ))}
              </p>
            )}
            <button
              className='universalButtonRed'
              onClick={() => onUpdate({ editorOpen: !item.editorOpen })}>
              {item.editorOpen ? "Close editor" : "Open editor"}
            </button>
          </div>
          {item.editorOpen && (
            <LocationQuickEditPanel
              key={item.createdLocationId}
              locationId={item.createdLocationId}
              currentBusinessID={currentBusinessID}
              userInformation={userInformation}
            />
          )}
        </div>
      )}
    </div>
  );
};

// Main tags, phone, booking link and review-site links. Most are pre-filled
// from Google / the website scan; suggestions found on the site are shown
// under each field so a different one can be picked with one click.
const DetailsSection = ({ form, result, onUpdateForm }) => {
  const searchName = encodeURIComponent(form.locationName || "");
  const city = encodeURIComponent(
    form.googleBusinessDetails?.address_components?.find((c) =>
      c.types?.includes("postal_town") || c.types?.includes("locality"),
    )?.long_name || "",
  );
  const tripadvisorInvalid =
    form.tripadvisorLink.trim() && !tripadvisorIdFromLink(form.tripadvisorLink);
  const yelpInvalid = form.yelpLink.trim() && !yelpIdFromLink(form.yelpLink);

  const suggestions = (list, current, field, label) => {
    const others = (list || []).filter((x) => x.value !== current);
    if (others.length === 0) return null;
    return (
      <div className='quickAddSuggestions'>
        <span className='quickAddHint'>{label}:</span>
        {others.map((x) => (
          <button
            key={x.value}
            className='quickAddChip'
            title={x.value}
            onClick={() => onUpdateForm({ [field]: x.value })}>
            {x.label}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className='quickReviewSection'>
      <div className='quickReviewSectionHeader'>Details</div>
      <div className='quickReviewSectionBody'>
        <div className='quickAddField'>
          <label className='editPageLabel'>Main tags</label>
          <input
            placeholder='Visible tags on the profile, comma-separated (e.g. Italian, Bar)'
            value={form.mainTags}
            onChange={(e) => onUpdateForm({ mainTags: e.target.value })}
          />
          <span className='quickAddHint'>
            Write them in the location's local language — they are translated
            to the other languages automatically when the location is created.
          </span>
        </div>

        <div className='quickAddGrid' style={{ marginTop: 15 }}>
          <div className='quickAddField'>
            <label className='editPageLabel'>Phone number</label>
            <input
              placeholder='+47 ...'
              value={form.phone}
              onChange={(e) => onUpdateForm({ phone: e.target.value })}
            />
            {suggestions(
              [
                ...(form.googleBusinessDetails?.international_phone_number
                  ? [form.googleBusinessDetails.international_phone_number]
                  : []),
                ...(result?.phones || []),
              ]
                .filter((v, i, a) => a.indexOf(v) === i)
                .map((v) => ({ value: v, label: v })),
              form.phone,
              "phone",
              "Found",
            )}
          </div>

          <div className='quickAddField'>
            <label className='editPageLabel'>Booking link</label>
            <input
              placeholder='https://... (table reservation page)'
              value={form.bookingLink}
              onChange={(e) => onUpdateForm({ bookingLink: e.target.value })}
            />
            {suggestions(
              (result?.bookingLinks || []).map((b) => ({
                value: b.url,
                label: b.provider ? `${b.provider} ✓` : b.text || b.url,
              })),
              form.bookingLink,
              "bookingLink",
              "Found on website",
            )}
            {result && result.bookingLinks.length === 0 && (
              <span className='quickAddHint'>No booking link found on the website.</span>
            )}
          </div>
        </div>

        <div className='quickAddGrid' style={{ marginTop: 15 }}>
          <div className='quickAddField'>
            <label className='editPageLabel'>Tripadvisor link</label>
            <div className='quickAddInline'>
              <input
                placeholder='https://www.tripadvisor.com/Restaurant_Review-...'
                value={form.tripadvisorLink}
                onChange={(e) => onUpdateForm({ tripadvisorLink: e.target.value })}
              />
              <a
                className='universalButton'
                target='_blank'
                rel='noreferrer'
                href={`https://www.google.com/search?q=${searchName}+${city}+site%3Atripadvisor.com`}>
                Search ↗
              </a>
            </div>
            {tripadvisorInvalid && (
              <span className='quickAddError'>
                Use the link to the location's own Tripadvisor page (it contains
                "-d" followed by numbers).
              </span>
            )}
          </div>

          <div className='quickAddField'>
            <label className='editPageLabel'>Yelp link</label>
            <div className='quickAddInline'>
              <input
                placeholder='https://www.yelp.com/biz/...'
                value={form.yelpLink}
                onChange={(e) => onUpdateForm({ yelpLink: e.target.value })}
              />
              <a
                className='universalButton'
                target='_blank'
                rel='noreferrer'
                href={`https://www.google.com/search?q=${searchName}+${city}+site%3Ayelp.com%2Fbiz`}>
                Search ↗
              </a>
            </div>
            {yelpInvalid && (
              <span className='quickAddError'>
                Use the location's Yelp page (https://www.yelp.com/biz/...).
              </span>
            )}
          </div>
        </div>
        <p className='quickAddHint' style={{ marginBottom: 0 }}>
          Ratings and reviews from Tripadvisor and Yelp are fetched when you
          create the location.
        </p>
      </div>
    </div>
  );
};

// Main image = the location's avatar. Best source is usually the Facebook or
// Instagram profile picture: we guess the Facebook one automatically, and
// otherwise you open the profile, right-click the picture → "Copy image",
// and paste it here (an image URL or a file works too).
const MainImagePicker = ({ mainImage, onChange, socialLinks, scanned, locationName }) => {
  const [urlInput, setUrlInput] = useState("");
  const [pasteError, setPasteError] = useState(null);

  const facebook = socialLinks?.facebook || [];
  const instagram = socialLinks?.instagram || [];
  const searchName = encodeURIComponent(locationName || "");

  const setFile = (file) => {
    if (!file || !file.type.startsWith("image/")) {
      setPasteError("That is not an image.");
      return;
    }
    setPasteError(null);
    onChange({ kind: "file", file, preview: URL.createObjectURL(file) });
  };

  const setUrl = (raw) => {
    const url = (raw || "").trim();
    if (!/^https?:\/\//i.test(url)) {
      setPasteError("Paste an image, or an image address starting with http.");
      return;
    }
    setPasteError(null);
    onChange({ kind: "url", url });
    setUrlInput("");
  };

  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find((i) => i.type.startsWith("image/"));
    if (imageItem) {
      e.preventDefault();
      setFile(imageItem.getAsFile());
      return;
    }
    const text = e.clipboardData?.getData("text");
    if (text) {
      e.preventDefault();
      setUrl(text);
    }
  };

  const previewSrc = mainImage
    ? mainImage.kind === "file"
      ? mainImage.preview
      : mainImage.url
    : null;

  return (
    <div className='quickReviewSection'>
      <div className='quickReviewSectionHeader'>Main image (profile picture)</div>
      <div className='quickReviewSectionBody quickAddMain'>
        <div className='quickAddMainPreview'>
          {previewSrc ? (
            <>
              <img
                src={previewSrc}
                alt=''
                referrerPolicy='no-referrer'
                onError={() => {
                  // A guessed Facebook picture that doesn't exist — just drop it.
                  if (mainImage?.auto) onChange(null);
                  else setPasteError("This image could not be loaded.");
                }}
              />
              <span className='quickAddHint'>
                {mainImage.auto
                  ? "From Facebook (auto)"
                  : mainImage.kind === "file"
                    ? "Pasted / uploaded"
                    : "From URL"}
              </span>
              <button className='quickAddLinkButton' onClick={() => onChange(null)}>
                Remove
              </button>
            </>
          ) : (
            <div className='quickAddMainEmpty'>No main image</div>
          )}
        </div>

        <div className='quickAddMainControls'>
          <div className='quickAddSocialLinks'>
            {facebook.map((f) => (
              <span key={f.url} className='quickAddSocialGroup'>
                <a href={f.url} target='_blank' rel='noreferrer' className='universalButton'>
                  Open Facebook ↗
                </a>
                {!(
                  mainImage?.kind === "url" &&
                  mainImage.url === facebookPictureUrl(f.pictureId)
                ) && (
                  <button
                    className='quickAddLinkButton'
                    onClick={() =>
                      onChange({ kind: "url", url: facebookPictureUrl(f.pictureId), auto: true })
                    }>
                    use its picture
                  </button>
                )}
              </span>
            ))}
            {instagram.map((i) => (
              <a
                key={i.url}
                href={i.url}
                target='_blank'
                rel='noreferrer'
                className='universalButton'>
                Open Instagram ↗
              </a>
            ))}
            {/* No Facebook link, but the Instagram handle is often the same
                page name on Facebook. Click-to-try only — a same-named page
                could belong to someone else, so we don't pick it on our own. */}
            {facebook.length === 0 &&
              instagram.map((i) => {
                const handle = i.url.split("/").filter(Boolean).pop();
                const url = facebookPictureUrl(handle);
                if (mainImage?.kind === "url" && mainImage.url === url) return null;
                return (
                  <button
                    key={`fb-guess-${handle}`}
                    className='quickAddLinkButton'
                    onClick={() => onChange({ kind: "url", url, auto: true })}>
                    try Facebook picture for @{handle}
                  </button>
                );
              })}
            {scanned && facebook.length === 0 && (
              <a
                href={`https://www.facebook.com/search/pages/?q=${searchName}`}
                target='_blank'
                rel='noreferrer'
                className='universalButton'>
                Search Facebook ↗
              </a>
            )}
            {scanned && instagram.length === 0 && (
              <a
                href={`https://www.google.com/search?q=${searchName}+site%3Ainstagram.com`}
                target='_blank'
                rel='noreferrer'
                className='universalButton'>
                Search Instagram ↗
              </a>
            )}
          </div>

          <div
            className='quickAddPasteZone'
            tabIndex={0}
            onPaste={onPaste}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files?.[0];
              if (file) setFile(file);
              else
                setUrl(
                  e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text"),
                );
            }}>
            <strong>Click here and press Ctrl+V</strong>
            <span className='quickAddHint'>
              On Facebook/Instagram: right-click the profile picture → "Copy
              image", then paste here. You can also drop an image file here.
            </span>
          </div>

          <div className='quickAddInline'>
            <input
              placeholder='...or paste an image address (https://...)'
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setUrl(urlInput)}
            />
            <button
              className='universalButton'
              disabled={!urlInput}
              onClick={() => setUrl(urlInput)}>
              Use
            </button>
            <label className='universalButton quickAddFileButton'>
              Choose file
              <input
                type='file'
                accept='image/*'
                onChange={(e) => {
                  setFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          {pasteError && <p className='quickAddError'>{pasteError}</p>}
        </div>
      </div>
    </div>
  );
};

export default QuickAddLocation;
